import React, { useState, useEffect } from 'react';
import { Settings, Lock, Bell, Shield, Trash2, Eye, EyeOff } from 'lucide-react';
import { Switch } from '../../components/ui/switch';
import { useAuth } from '../../context/AuthContext';
import { profileAPI } from '../../services/api';
import { toast } from 'sonner';

// ─── Section Card ─────────────────────────────────────────────────────────────
const SectionCard = ({ icon: Icon, title, children }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden mb-5">
    <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100" style={{ backgroundColor: '#f5f0e8' }}>
      <Icon size={18} style={{ color: '#1a5d3a' }} />
      <h2 className="text-base font-semibold" style={{ fontFamily: 'Playfair Display, serif', color: '#1a5d3a' }}>
        {title}
      </h2>
    </div>
    <div className="px-6 py-5">{children}</div>
  </div>
);

// ─── Toggle Row ───────────────────────────────────────────────────────────────
const ToggleRow = ({ label, description, checked, onChange, testId }) => (
  <div className="flex items-center justify-between py-3 border-b border-gray-50 last:border-b-0" data-testid={testId}>
    <div>
      <div className="text-sm font-medium" style={{ color: '#1a1a1a', fontFamily: 'DM Sans, sans-serif' }}>{label}</div>
      {description && <div className="text-xs mt-0.5" style={{ color: '#9CA3AF', fontFamily: 'DM Sans, sans-serif' }}>{description}</div>}
    </div>
    <Switch
      checked={checked}
      onCheckedChange={onChange}
      className="data-[state=checked]:bg-green-800"
    />
  </div>
);

// ─── AccountSettings ──────────────────────────────────────────────────────────
const AccountSettings = () => {
  const { user, updateUser } = useAuth();

  // ── Password form state ────────────────────────────────────────────────────
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwShow, setPwShow] = useState({ current: false, new: false, confirm: false });
  const [pwLoading, setPwLoading] = useState(false);

  // ── Notification prefs state ───────────────────────────────────────────────
  const [notifs, setNotifs] = useState({
    notification_email: true,
    notification_scholarship_alerts: true,
    notification_college_alerts: true,
  });

  // ── Privacy prefs state ────────────────────────────────────────────────────
  const [privacy, setPrivacy] = useState({
    privacy_visible_to_colleges: false,
    available_for_qc411: false,
  });

  // ── Delete account state ───────────────────────────────────────────────────
  const [deleteInput, setDeleteInput] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (user) {
      setNotifs({
        notification_email: user.notification_email ?? true,
        notification_scholarship_alerts: user.notification_scholarship_alerts ?? true,
        notification_college_alerts: user.notification_college_alerts ?? true,
      });
      setPrivacy({
        privacy_visible_to_colleges: user.privacy_visible_to_colleges ?? false,
        available_for_qc411: user.available_for_qc411 ?? false,
      });
    }
  }, [user]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handlePwChange = async (e) => {
    e.preventDefault();
    if (!pwForm.current_password || !pwForm.new_password || !pwForm.confirm_password) {
      toast.error('All password fields are required');
      return;
    }
    setPwLoading(true);
    try {
      await profileAPI.changePassword(pwForm);
      toast.success('Password changed successfully');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwLoading(false);
    }
  };

  const saveNotifPref = async (key, val) => {
    const updated = { ...notifs, [key]: val };
    setNotifs(updated);
    try {
      await profileAPI.updateProfile({ [key]: val });
      updateUser({ [key]: val });
      toast.success('Preference saved');
    } catch {
      toast.error('Failed to save preference');
    }
  };

  const savePrivacyPref = async (key, val) => {
    const updated = { ...privacy, [key]: val };
    setPrivacy(updated);
    try {
      await profileAPI.updateProfile({ [key]: val });
      updateUser({ [key]: val });
      toast.success('Privacy setting saved');
    } catch {
      toast.error('Failed to save setting');
    }
  };

  const PwInput = ({ field, placeholder, showKey }) => (
    <div className="relative">
      <input
        type={pwShow[showKey] ? 'text' : 'password'}
        value={pwForm[field]}
        onChange={(e) => setPwForm((p) => ({ ...p, [field]: e.target.value }))}
        placeholder={placeholder}
        className="w-full px-4 py-2.5 pr-10 rounded-lg text-sm border outline-none transition-colors"
        style={{ borderColor: '#d1d5db', fontFamily: 'DM Sans, sans-serif', color: '#1a1a1a' }}
        data-testid={`password-input-${field}`}
        onFocus={(e) => (e.target.style.borderColor = '#1a5d3a')}
        onBlur={(e) => (e.target.style.borderColor = '#d1d5db')}
      />
      <button
        type="button"
        onClick={() => setPwShow((p) => ({ ...p, [showKey]: !p[showKey] }))}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        tabIndex={-1}
        data-testid={`password-toggle-${field}`}
      >
        {pwShow[showKey] ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );

  return (
    <div className="min-h-screen p-6 md:p-8" style={{ backgroundColor: '#f5f0e8' }} data-testid="account-settings-page">
      <div className="max-w-2xl mx-auto">
        {/* Page header */}
        <div className="flex items-center gap-3 mb-7">
          <Settings size={22} style={{ color: '#1a5d3a' }} />
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: '#1a5d3a' }}>
              Account Settings
            </h1>
            <p className="text-xs mt-0.5" style={{ color: '#9CA3AF', fontFamily: 'DM Sans, sans-serif' }}>
              Manage your security, notifications, and privacy.
            </p>
          </div>
        </div>

        {/* ── 1. Account Security ────────────────────────────────────────── */}
        <SectionCard icon={Lock} title="Account Security">
          {/* Email display */}
          <div className="mb-5 p-3 rounded-lg" style={{ backgroundColor: '#f5f0e8' }}>
            <div className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#9CA3AF' }}>Email address</div>
            <div className="text-sm font-medium" style={{ color: '#1a1a1a', fontFamily: 'DM Sans, sans-serif' }}>
              {user?.email}
              <span className="ml-2 text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#e5e7eb', color: '#9CA3AF' }}>Read only</span>
            </div>
          </div>

          {/* Change password form */}
          <form onSubmit={handlePwChange} className="space-y-3" data-testid="change-password-form">
            <div className="text-sm font-semibold mb-2" style={{ color: '#1a1a1a', fontFamily: 'DM Sans, sans-serif' }}>
              Change Password
            </div>
            <PwInput field="current_password" placeholder="Current password" showKey="current" />
            <PwInput field="new_password" placeholder="New password (min 8 chars)" showKey="new" />
            <PwInput field="confirm_password" placeholder="Confirm new password" showKey="confirm" />
            <button
              type="submit"
              disabled={pwLoading}
              className="w-full py-2.5 rounded-lg text-sm font-semibold text-white transition-opacity disabled:opacity-60"
              style={{ backgroundColor: '#1a5d3a', fontFamily: 'DM Sans, sans-serif' }}
              data-testid="change-password-submit"
            >
              {pwLoading ? 'Saving...' : 'Update Password'}
            </button>
          </form>
        </SectionCard>

        {/* ── 2. Notification Preferences ────────────────────────────────── */}
        <SectionCard icon={Bell} title="Notification Preferences">
          <ToggleRow
            label="Email notifications"
            description="Receive general platform updates by email."
            checked={notifs.notification_email}
            onChange={(v) => saveNotifPref('notification_email', v)}
            testId="toggle-notification-email"
          />
          <ToggleRow
            label="Scholarship deadline alerts"
            description="Get notified when saved scholarships are closing soon."
            checked={notifs.notification_scholarship_alerts}
            onChange={(v) => saveNotifPref('notification_scholarship_alerts', v)}
            testId="toggle-scholarship-alerts"
          />
          <ToggleRow
            label="College match alerts"
            description="Hear about new colleges that match your profile."
            checked={notifs.notification_college_alerts}
            onChange={(v) => saveNotifPref('notification_college_alerts', v)}
            testId="toggle-college-alerts"
          />
        </SectionCard>

        {/* ── 3. Privacy Settings ────────────────────────────────────────── */}
        <SectionCard icon={Shield} title="Privacy Settings">
          <ToggleRow
            label="Profile visible to colleges"
            description="Allow partner colleges to view your profile and reach out."
            checked={privacy.privacy_visible_to_colleges}
            onChange={(v) => savePrivacyPref('privacy_visible_to_colleges', v)}
            testId="toggle-visible-to-colleges"
          />
          <ToggleRow
            label="Available for QC 411"
            description="Let enrollment officers request a QuadConnect session with you."
            checked={privacy.available_for_qc411}
            onChange={(v) => savePrivacyPref('available_for_qc411', v)}
            testId="toggle-qc411-settings"
          />
        </SectionCard>

        {/* ── 4. Danger Zone ────────────────────────────────────────────── */}
        <div
          className="bg-white rounded-xl shadow-sm overflow-hidden mb-5"
          style={{ border: '1.5px solid #fca5a5' }}
          data-testid="danger-zone-card"
        >
          <div className="flex items-center gap-3 px-6 py-4 border-b" style={{ backgroundColor: '#fff5f5', borderColor: '#fca5a5' }}>
            <Trash2 size={18} style={{ color: '#dc2626' }} />
            <h2 className="text-base font-semibold" style={{ fontFamily: 'Playfair Display, serif', color: '#dc2626' }}>
              Danger Zone
            </h2>
          </div>
          <div className="px-6 py-5">
            {!showDeleteConfirm ? (
              <div>
                <p className="text-sm mb-4" style={{ color: '#6B7280', fontFamily: 'DM Sans, sans-serif' }}>
                  Permanently deleting your account will remove all your data, saved colleges, scholarships, and profile information. This action cannot be undone.
                </p>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: '#dc2626', fontFamily: 'DM Sans, sans-serif' }}
                  data-testid="delete-account-btn"
                >
                  Delete my account
                </button>
              </div>
            ) : (
              <div data-testid="delete-account-confirm">
                <p className="text-sm font-medium mb-1" style={{ color: '#dc2626', fontFamily: 'DM Sans, sans-serif' }}>
                  Type <strong>DELETE</strong> to confirm
                </p>
                <p className="text-xs mb-3" style={{ color: '#9CA3AF' }}>
                  To delete your account, please contact{' '}
                  <a href="mailto:support@studentsignal.com" className="underline" style={{ color: '#1a5d3a' }}>
                    support@studentsignal.com
                  </a>
                  .
                </p>
                <div className="flex gap-2">
                  <input
                    value={deleteInput}
                    onChange={(e) => setDeleteInput(e.target.value)}
                    placeholder="Type DELETE"
                    className="flex-1 px-3 py-2 rounded-lg text-sm border outline-none"
                    style={{ borderColor: '#fca5a5', fontFamily: 'DM Sans, sans-serif' }}
                    data-testid="delete-confirm-input"
                  />
                  <button
                    disabled={deleteInput !== 'DELETE'}
                    className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-40 transition-opacity"
                    style={{ backgroundColor: '#dc2626' }}
                    data-testid="delete-confirm-btn"
                    onClick={() => toast.info('Please contact support@studentsignal.com to delete your account.')}
                  >
                    Confirm
                  </button>
                  <button
                    onClick={() => { setShowDeleteConfirm(false); setDeleteInput(''); }}
                    className="px-4 py-2 rounded-lg text-sm font-semibold border border-gray-200 hover:bg-gray-50 transition-colors"
                    style={{ color: '#6B7280', fontFamily: 'DM Sans, sans-serif' }}
                    data-testid="delete-cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default AccountSettings;
